<template>
  <div>
    <p>
      {{ $t('page-info.statistics') }}
    </p>
    <p class="border-bottom pb-3">
      <b>{{ $t('text.hint') }}:</b> {{ $t('page-info.statistics-hint') }}
    </p>
    <b-card title="Share" class="mb-4">
      <b-input-group>
        <b-input type="text" :placeholder="$t('text.your-name')" v-model="shareName" />
        <b-input-group-append>
          <b-button variant="outline-info" @click="copyShareLink" ref="copyLinkBtn"
                    :disabled="shareName.length === 0">
            {{ $t('text.copy-link') }}
          </b-button>
          <b-tooltip :target="() => $refs.copyLinkBtn" variant="success" triggers="blur" ref="copyLinkTooltip">
            {{ $t('text.copied') }}
          </b-tooltip>
          <b-button variant="info" v-if="hasShare" @click="share"
                    :disabled="shareName.length === 0">
            {{ $t('text.share') }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-card-text class="mt-2" v-if="shareName.length > 0">
        <a :href="shareLink" ref="shareLink">{{ shareLink }}</a>
      </b-card-text>
    </b-card>

    <section v-for="primaryCategory in $root.categories" :key="primaryCategory[0]">
      <h1>{{ $t('category.' + primaryCategory[0] + '._') }}</h1>
      <b-table-lite :fields="fields" :items="itemsFor(...primaryCategory, comparisonModels)"
                    :responsive="true"
                    @row-clicked="(item) => item._showDetails = !item._showDetails">
        <template v-slot:cell(category)="data">
          {{ $t('category.' + data.value[0] + '.' + data.value[1]) }}
        </template>
        <template v-slot:cell()="data">
          <span class="text-nowrap">{{ data.value }}</span>
        </template>
        <template v-slot:head()="data">
          {{ $t(data.label) }}
        </template>
        <template v-slot:head(friendOwn)>
          {{ $t('text.friend-owns', { name: comparisonName }) }}
        </template>
        <template v-slot:head(inYourRegion)="data">
          {{ $t(data.label, { region: $t('region.' + $store.state.currentRegion) }) }}
        </template>
        <template v-slot:row-details="row">
          <template v-if="hasComparison">
            <div v-for="selection in ['both', 'only-friend', 'only-you']" :key="selection">
              <template v-if="toolsFor(row.item.category[1], selection).length > 0">
                <h5 v-if="selection === 'both'">{{ $t('text.you-both-own') }}</h5>
                <h5 v-else-if="selection === 'only-friend'">{{ $t('text.only-your-friend-owns') }}</h5>
                <h5 v-else-if="selection === 'only-you'">{{ $t('text.only-you-own') }}</h5>
                <b-row>
                  <b-col cols="3" md="2" lg="1"
                         v-for="tool in toolsFor(row.item.category[1], selection)" :key="'own-' + tool.model">
                    <router-link :to="{ name: 'product', params: { model: tool.model } }">
                      <img :src="tool.image" :alt="tool.model" class="w-100">
                    </router-link>
                  </b-col>
                </b-row>
              </template>
            </div>
          </template>
          <template v-else>
            <b-row>
              <b-col cols="3" md="2" lg="1"
                     v-for="tool in toolsFor(row.item.category[1], 'you')" :key="'own-' + tool.model">
                <router-link :to="{ name: 'product', params: { model: tool.model } }">
                  <img :src="tool.image" :alt="tool.model" class="w-100">
                </router-link>
              </b-col>
            </b-row>
          </template>
        </template>
      </b-table-lite>
    </section>
  </div>
</template>

<script>
function serializeComparison(name, models) {
  return [name, ...models].join(',')
}
function deserializeComparison(string) {
  const parts = string.split(',')
  return [parts[0], parts.slice(1)]
}

export default {
  name: 'Statistics',
  props: ['comparison'],
  computed: {
    shareName: {
      get () { return this.$store.state.shareName },
      set (value) { this.$store.commit('setShareName', value) }
    },
    shareLink () {
      const path = this.$router.resolve({ name: 'compare', params: {
        comparison: serializeComparison(this.shareName, this.$store.state.ownedModels)
      } }).href
      return location.origin + path
    },
    hasShare () {
      return !!navigator.share
    },
    hasComparison () {
      return !!this.comparison
    },
    comparisonName () {
      return this.hasComparison ? deserializeComparison(this.comparison)[0] : null
    },
    comparisonModels () {
      return this.hasComparison ? deserializeComparison(this.comparison)[1] : null
    },
    fields () {
      const extraFields = []
      if (this.hasComparison) {
        extraFields.push({
          key: 'friendOwn',
          formatter (value) {
            return `${value[0]} (${value[1]} %)`
          }
        })
      }
      return [
        {
          key: 'category',
          label: 'text.category',
          stickyColumn: true
        },
        {
          key: 'youOwn',
          formatter (value) {
            return `${value[0]} (${value[1]} %)`
          },
          label: 'text.you-own'
        },
        ...extraFields,
        {
          key: 'inYourRegion',
          label: 'text.in-your-region'
        },
        {
          key: 'globally',
          label: 'text.globally'
        }
      ]
    }
  },
  methods: {
    itemsFor (primary, categories, comparisonModels) {
      return categories.map(cat => {
        const tools = Object.values(this.$root.tools).filter(e => e.category === cat)
        const inYourRegion = tools.filter(e => this.$store.state.currentRegion in e.url)
        const youOwn = tools.filter(e => this.$store.state.ownedModels.includes(e.model), this)
        const row = {
          category: [primary, cat],
          youOwn: [youOwn.length, Math.round(youOwn.length / tools.length * 100)],
          inYourRegion: inYourRegion.length,
          globally: tools.length,
          _showDetails: false
        }
        if (comparisonModels) {
          const friendOwn = tools.filter(e => comparisonModels.includes(e.model))
          return {
            ...row,
            friendOwn: [friendOwn.length, Math.round(friendOwn.length / tools.length * 100)]
          }
        } else {
          return row
        }
      })
    },
    toolsFor (category, selection) {
      const tools = Object.values(this.$root.tools).filter(e => e.category === category)
      const friend = this.comparisonModels
      const you = this.$store.state.ownedModels
      if (selection === 'friend') {
        return tools.filter(e => friend.includes(e.model))
      } else if (selection === 'you') {
        return tools.filter(e => you.includes(e.model))
      } else if (selection === 'both') {
        return tools.filter(e => friend.includes(e.model) && you.includes(e.model))
      } else if (selection === 'only-friend') {
        return tools.filter(e => friend.includes(e.model) && !you.includes(e.model))
      } else if (selection === 'only-you') {
        return tools.filter(e => !friend.includes(e.model) && you.includes(e.model))
      }
    },
    copyShareLink () {
      const range = document.createRange()
      range.selectNode(this.$refs.shareLink)
      window.getSelection().removeAllRanges()
      window.getSelection().addRange(range)
      try {
        const successful = document.execCommand('copy')
        if (successful) {
          console.warn(this.$refs.copyLinkTooltip)
          this.$refs.copyLinkTooltip.doOpen()
        }
      } catch (err) {
        // no-op
      }
      window.getSelection().removeAllRanges()
    },
    share () {
      navigator.share({
        url: this.shareLink,
        title: this.$t('text.share-title', { name: this.shareName })
      })
    }
  },
  metaInfo () {
    return {
      title: this.$t('menu.page.statistics'),
      meta: [
        { name: 'description', content: this.$t('page-info.statistics') }
      ]
    }
  }
}
</script>

<style lang="scss">

</style>